import React, { useState, useEffect } from "react";
import axios from "../api/axios";
import {
  FiShoppingBag, FiLoader, FiSearch, FiChevronLeft, FiChevronRight,
  FiDollarSign, FiPackage, FiMapPin, FiPhone, FiCheckCircle,
  FiClock, FiXCircle
} from "react-icons/fi";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .mo-root {
    font-family: 'Montserrat', sans-serif;
    padding: 24px;
    max-width: 80rem;
    margin: 0 auto;
    min-height: 100vh;
    background: linear-gradient(135deg, #f1f5f9 0%, #eff6ff 50%, #f1f5f9 100%);
  }

  @media (max-width: 640px) {
    .mo-root {
      padding: 16px;
    }
  }

  /* Header */
  .mo-header {
    margin-bottom: 32px;
  }

  .mo-title {
    font-size: 28px;
    font-weight: 700;
    background: linear-gradient(135deg, #1e293b, #475569);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 8px;
  }

  @media (min-width: 640px) {
    .mo-title {
      font-size: 30px;
    }
  }

  .mo-subtitle {
    color: #6b7280;
  }

  /* Filters */
  .mo-filters {
    margin-bottom: 24px;
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .mo-search-wrap {
    position: relative;
    flex: 1;
    min-width: 200px;
  }

  .mo-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
  }

  .mo-search-input {
    width: 100%;
    padding: 12px 16px 12px 40px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Montserrat', sans-serif;
    outline: none;
    transition: all 0.2s;
  }

  .mo-search-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  .mo-filter-select {
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Montserrat', sans-serif;
    outline: none;
    transition: all 0.2s;
    background: #ffffff;
    cursor: pointer;
  }

  .mo-filter-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  /* Orders List */
  .mo-orders {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .mo-empty {
    text-align: center;
    padding: 48px 0;
    color: #6b7280;
  }

  .mo-empty-icon {
    font-size: 36px;
    margin: 0 auto 16px;
    color: #d1d5db;
  }

  .mo-empty-text {
    margin: 0;
  }

  /* Order Card */
  .mo-order-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .mo-order-body {
    padding: 24px;
  }

  .mo-order-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .mo-order-info {
    flex: 1;
  }

  .mo-order-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }

  .mo-order-number {
    font-weight: 600;
    color: #1f2937;
  }

  .mo-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
  }

  .mo-status-delivered {
    background: #d1fae5;
    color: #065f46;
  }

  .mo-status-processing {
    background: #fef3c7;
    color: #92400e;
  }

  .mo-status-cancelled {
    background: #fee2e2;
    color: #991b1b;
  }

  .mo-status-pending {
    background: #f3f4f6;
    color: #374151;
  }

  .mo-status-icon-delivered { color: #22c55e; }
  .mo-status-icon-processing { color: #eab308; }
  .mo-status-icon-cancelled { color: #ef4444; }
  .mo-status-icon-pending { color: #6b7280; }

  .mo-order-date {
    font-size: 14px;
    color: #6b7280;
  }

  .mo-toggle-btn {
    background: none;
    border: none;
    color: #3b82f6;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 14px;
    transition: color 0.2s;
    padding: 4px 8px;
  }

  .mo-toggle-btn:hover {
    color: #1d4ed8;
  }

  /* Order Stats */
  .mo-stats {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  @media (min-width: 768px) {
    .mo-stats {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .mo-stat-item {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #6b7280;
  }

  .mo-stat-item svg {
    margin-right: 8px;
    flex-shrink: 0;
  }

  /* Mobile Store Info */
  .mo-store-info {
    background: #f9fafb;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
  }

  .mo-store-label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
  }

  .mo-store-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .mo-store-name {
    font-size: 14px;
    color: #6b7280;
  }

  .mo-store-id {
    font-size: 14px;
    color: #6b7280;
  }

  .mo-store-phone {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #6b7280;
  }

  .mo-store-phone svg {
    margin-right: 8px;
    flex-shrink: 0;
  }

  /* Expanded Details */
  .mo-expanded {
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
    margin-top: 16px;
  }

  .mo-items-section {
    margin-bottom: 16px;
  }

  .mo-items-label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
  }

  .mo-items-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .mo-item-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f9fafb;
    border-radius: 8px;
    padding: 12px;
  }

  .mo-item-info {
    display: flex;
    flex-direction: column;
  }

  .mo-item-name {
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
  }

  .mo-item-detail {
    font-size: 12px;
    color: #6b7280;
  }

  .mo-item-total {
    text-align: right;
  }

  .mo-item-amount {
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
  }

  .mo-item-pv {
    font-size: 12px;
    color: #6b7280;
  }

  /* Delivery Address */
  .mo-delivery-section {
    margin-bottom: 16px;
  }

  .mo-delivery-label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
  }

  .mo-delivery-address {
    display: flex;
    align-items: flex-start;
    font-size: 14px;
    color: #6b7280;
  }

  .mo-delivery-address svg {
    margin-right: 8px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  /* Dates Grid */
  .mo-dates {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    font-size: 14px;
  }

  .mo-date-label {
    color: #6b7280;
  }

  .mo-date-value {
    color: #1f2937;
  }

  /* Pagination */
  .mo-pagination {
    margin-top: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .mo-page-btn {
    padding: 8px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mo-page-btn:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .mo-page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .mo-page-info {
    padding: 8px 16px;
  }

  /* Loading State */
  .mo-loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f1f5f9 0%, #eff6ff 50%, #f1f5f9 100%);
  }

  .mo-loading-content {
    text-align: center;
  }

  .mo-spinner {
    animation: mo-spin 1s linear infinite;
    font-size: 36px;
    color: #3b82f6;
    margin: 0 auto 16px;
    display: block;
  }

  @keyframes mo-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .mo-loading-text {
    color: #6b7280;
  }

  /* Error State */
  .mo-error {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f1f5f9 0%, #eff6ff 50%, #f1f5f9 100%);
  }

  .mo-error-content {
    text-align: center;
    color: #ef4444;
  }

  .mo-error-text {
    margin-bottom: 16px;
  }

  .mo-retry-btn {
    padding: 8px 16px;
    background: #3b82f6;
    color: #ffffff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    transition: background 0.2s;
  }

  .mo-retry-btn:hover {
    background: #2563eb;
  }

  /* Responsive */
  @media (max-width: 640px) {
    .mo-order-body {
      padding: 16px;
    }

    .mo-order-title-row {
      flex-direction: column;
      align-items: flex-start;
    }

    .mo-stats {
      grid-template-columns: 1fr;
    }

    .mo-dates {
      grid-template-columns: 1fr;
    }

    .mo-filters {
      flex-direction: column;
    }

    .mo-search-wrap {
      min-width: unset;
    }

    .mo-item-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .mo-item-total {
      text-align: left;
      width: 100%;
    }
  }
`;

const MyOrders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: currentPage,
          limit: 20,
          ...(statusFilter !== "all" && { status: statusFilter })
        });
        const { data } = await axios.get(`/puap/my-orders?${params}`);
        setOrders(data.orders);
        setTotalPages(data.totalPages);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load orders");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [currentPage, statusFilter]);

  const filteredOrders = orders.filter(o => {
    const q = searchTerm.toLowerCase();
    return (
      (o.invoiceNumber?.toLowerCase() || "").includes(q) ||
      (o.mobileStoreId?.shopName?.toLowerCase() || "").includes(q) ||
      (o.mobileStoreId?.uniqueId?.toLowerCase() || "").includes(q)
    );
  });

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return <FiCheckCircle className="mo-status-icon-delivered" />;
      case "processing":
        return <FiClock className="mo-status-icon-processing" />;
      case "cancelled":
        return <FiXCircle className="mo-status-icon-cancelled" />;
      default:
        return <FiClock className="mo-status-icon-pending" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "mo-status-delivered";
      case "processing":
        return "mo-status-processing";
      case "cancelled":
        return "mo-status-cancelled";
      default:
        return "mo-status-pending";
    }
  };

  if (loading) {
    return (
      <div className="mo-loading">
        <style>{styles}</style>
        <div className="mo-loading-content">
          <FiLoader className="mo-spinner" />
          <p className="mo-loading-text">Loading your orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mo-error">
        <style>{styles}</style>
        <div className="mo-error-content">
          <p className="mo-error-text">{error}</p>
          <button onClick={() => window.location.reload()} className="mo-retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mo-root">
      <style>{styles}</style>

      <div className="mo-header">
        <h1 className="mo-title">My Orders</h1>
        <p className="mo-subtitle">View all your orders with complete details</p>
      </div>

      <div className="mo-filters">
        <div className="mo-search-wrap">
          <FiSearch className="mo-search-icon" />
          <input
            type="text"
            placeholder="Search by invoice or store..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mo-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="mo-filter-select"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="mo-orders">
        {filteredOrders.length === 0 ? (
          <div className="mo-empty">
            <FiShoppingBag className="mo-empty-icon" />
            <p className="mo-empty-text">No orders found</p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order._id} className="mo-order-card">
              <div className="mo-order-body">
                <div className="mo-order-header">
                  <div className="mo-order-info">
                    <div className="mo-order-title-row">
                      <h3 className="mo-order-number">
                        Order #{order.invoiceNumber || "N/A"}
                      </h3>
                      <span className={`mo-status-badge ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                        {order.status}
                      </span>
                    </div>
                    <p className="mo-order-date">
                      {order.orderDate ? new Date(order.orderDate).toLocaleString() : "N/A"}
                    </p>
                  </div>
                  <button
                    onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                    className="mo-toggle-btn"
                  >
                    {expandedOrder === order._id ? "Hide Details" : "View Details"}
                  </button>
                </div>

                <div className="mo-stats">
                  <div className="mo-stat-item">
                    <FiDollarSign />
                    <span>Total: ₹{order.totalAmount?.toLocaleString()}</span>
                  </div>
                  <div className="mo-stat-item">
                    <FiPackage />
                    <span>Total PV: {order.totalPv}</span>
                  </div>
                  <div className="mo-stat-item">
                    <FiShoppingBag />
                    <span>
                      {order.items?.length || 1} item{(order.items?.length || 1) > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                {order.mobileStoreId && (
                  <div className="mo-store-info">
                    <div className="mo-store-label">Mobile Store</div>
                    <div className="mo-store-details">
                      <div className="mo-store-name">
                        {order.mobileStoreId.shopName || order.mobileStoreId.name}
                      </div>
                      <div className="mo-store-id">
                        ID: {order.mobileStoreId.uniqueId}
                      </div>
                      {order.mobileStoreId.mobile && (
                        <div className="mo-store-phone">
                          <FiPhone size={14} />
                          {order.mobileStoreId.mobile}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {expandedOrder === order._id && (
                  <div className="mo-expanded">
                    <div className="mo-items-section">
                      <div className="mo-items-label">Items</div>
                      <div className="mo-items-list">
                        {order.items && order.items.length > 0 ? (
                          order.items.map((item, idx) => (
                            <div key={idx} className="mo-item-row">
                              <div className="mo-item-info">
                                <div className="mo-item-name">
                                  {item.product?.name || "Unknown Product"}
                                </div>
                                <div className="mo-item-detail">
                                  Qty: {item.quantity} × ₹{item.sp}
                                </div>
                              </div>
                              <div className="mo-item-total">
                                <div className="mo-item-amount">
                                  ₹{item.totalAmount?.toLocaleString()}
                                </div>
                                <div className="mo-item-pv">PV: {item.pv}</div>
                              </div>
                            </div>
                          ))
                        ) : order.product ? (
                          <div className="mo-item-row">
                            <div className="mo-item-info">
                              <div className="mo-item-name">
                                {order.product.name || "Unknown Product"}
                              </div>
                              <div className="mo-item-detail">
                                Qty: {order.quantity} × ₹{order.sp}
                              </div>
                            </div>
                            <div className="mo-item-total">
                              <div className="mo-item-amount">
                                ₹{order.totalAmount?.toLocaleString()}
                              </div>
                              <div className="mo-item-pv">PV: {order.pv}</div>
                            </div>
                          </div>
                        ) : (
                          <div className="mo-item-detail">No items available</div>
                        )}
                      </div>
                    </div>

                    {order.deliveryAddress && (
                      <div className="mo-delivery-section">
                        <div className="mo-delivery-label">Delivery Address</div>
                        <div className="mo-delivery-address">
                          <FiMapPin />
                          <span>{order.deliveryAddress}</span>
                        </div>
                      </div>
                    )}

                    <div className="mo-dates">
                      {order.processingDate && (
                        <div>
                          <span className="mo-date-label">Processing: </span>
                          <span className="mo-date-value">
                            {new Date(order.processingDate).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {order.deliveryDate && (
                        <div>
                          <span className="mo-date-label">Delivered: </span>
                          <span className="mo-date-value">
                            {new Date(order.deliveryDate).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {order.completedDate && (
                        <div>
                          <span className="mo-date-label">Completed: </span>
                          <span className="mo-date-value">
                            {new Date(order.completedDate).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="mo-pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="mo-page-btn"
          >
            <FiChevronLeft />
          </button>
          <span className="mo-page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="mo-page-btn"
          >
            <FiChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default MyOrders;